/**
 * Teardrop-pin marker icons (FAIL-476) as MapLibre SDF images.
 *
 * A MapLibre `circle` layer can be tinted by a data expression — that's how
 * color-by works today. A teardrop pin can't be a circle; it has to be an
 * *icon*, and the only icon MapLibre recolors by data is an **SDF** (signed
 * distance field) icon. So the pin is registered with `map.addImage(..., {sdf})`
 * and a `symbol` layer tints it with the same color expression the circle uses.
 *
 * The SDF is generated here (no `@mapbox/tiny-sdf` dependency) so editor, public
 * URL, embed, and OG all draw the identical pin. `sdfFromMask` — the core
 * (2×2-supersampled coverage → Felzenszwalb EDT → tiny-sdf's alpha encoding) —
 * is pure and unit-tested; the pin/dot *shapes* are analytic (no canvas), so the
 * whole module runs in Node.
 */

/** A registerable image: RGBA bytes (RGB white; SDF distance in the alpha channel). */
export interface SdfImage {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

/** MapLibre-style SDF encoding constants (tiny-sdf defaults — the values the shader expects). */
const SDF_RADIUS = 8;
const SDF_CUTOFF = 0.25;
const INF = 1e20;

/**
 * The SDF image ids the renderer registers + references. `pin` is the solid
 * teardrop body (both `pin` and `pin-solid` shapes use it); `pin-dot` is the
 * white head dot the `pin` shape stacks on top.
 */
export const PIN_IMAGE = "pz-pin";
export const PIN_DOT_IMAGE = "pz-pin-dot";

/**
 * Native CSS height of the generated pin (its bottom edge is the tip). Icons are
 * registered at `PIXEL_RATIO`, so `icon-size = desiredHeightPx / PIN_NATIVE_HEIGHT`.
 */
export const PIN_NATIVE_HEIGHT = 40;
export const PIN_PIXEL_RATIO = 2;

/* ------------------------------------------------------------------ */
/* Felzenszwalb & Huttenlocher exact Euclidean distance transform      */
/* (transcribed from mapbox/tiny-sdf, ISC — the proven implementation)  */
/* ------------------------------------------------------------------ */

function edt1d(
  grid: Float64Array,
  offset: number,
  stride: number,
  length: number,
  f: Float64Array,
  v: Int16Array,
  z: Float64Array,
): void {
  v[0] = 0;
  z[0] = -INF;
  z[1] = INF;
  f[0] = grid[offset]!;

  for (let q = 1, k = 0, s = 0; q < length; q++) {
    f[q] = grid[offset + q * stride]!;
    const q2 = q * q;
    do {
      const r = v[k]!;
      s = (f[q]! - f[r]! + q2 - r * r) / (q - r) / 2;
    } while (s <= z[k]! && --k > -1);
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = INF;
  }

  for (let q = 0, k = 0; q < length; q++) {
    while (z[k + 1]! < q) k++;
    const r = v[k]!;
    const qr = q - r;
    grid[offset + q * stride] = f[r]! + qr * qr;
  }
}

/** In-place 2D squared-distance transform (columns then rows). */
function edt(grid: Float64Array, width: number, height: number): void {
  const size = Math.max(width, height);
  const f = new Float64Array(size);
  const v = new Int16Array(size);
  const z = new Float64Array(size + 1);
  for (let x = 0; x < width; x++) edt1d(grid, x, width, height, f, v, z);
  for (let y = 0; y < height; y++) edt1d(grid, y * width, 1, width, f, v, z);
}

/* ------------------------------------------------------------------ */
/* SDF from an analytic mask                                            */
/* ------------------------------------------------------------------ */

/**
 * Rasterize `inside(x, y)` (device-pixel coordinates, sampled at pixel centers
 * with 2×2 supersampling for anti-aliased edges) into an SDF image. The alpha
 * channel carries the distance field MapLibre samples; RGB is white so
 * `icon-color` fully controls the tint.
 */
export function sdfFromMask(
  width: number,
  height: number,
  inside: (x: number, y: number) => boolean,
  radius: number = SDF_RADIUS,
  cutoff: number = SDF_CUTOFF,
): SdfImage {
  const n = width * height;
  const gridOuter = new Float64Array(n);
  const gridInner = new Float64Array(n);

  // 2×2 supersampled coverage per pixel → tiny-sdf's partial-cell cost seeding.
  const offs = [0.25, 0.75];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let hits = 0;
      for (const dy of offs) for (const dx of offs) if (inside(x + dx, y + dy)) hits++;
      const a = hits / 4;
      const i = y * width + x;
      if (a === 1) {
        gridOuter[i] = 0;
        gridInner[i] = INF;
      } else if (a === 0) {
        gridOuter[i] = INF;
        gridInner[i] = 0;
      } else {
        const outer = Math.max(0, 0.5 - a);
        const inner = Math.max(0, a - 0.5);
        gridOuter[i] = outer * outer;
        gridInner[i] = inner * inner;
      }
    }
  }

  edt(gridOuter, width, height);
  edt(gridInner, width, height);

  const data = new Uint8ClampedArray(n * 4);
  for (let i = 0; i < n; i++) {
    // Signed distance: positive outside the shape, negative inside.
    const d = Math.sqrt(gridOuter[i]!) - Math.sqrt(gridInner[i]!);
    const alpha = Math.round(255 - 255 * (d / radius + cutoff));
    const j = i * 4;
    data[j] = 255;
    data[j + 1] = 255;
    data[j + 2] = 255;
    data[j + 3] = alpha < 0 ? 0 : alpha > 255 ? 255 : alpha;
  }
  return { width, height, data };
}

/* ------------------------------------------------------------------ */
/* Pin geometry (analytic — a head disc + a tip via tangent lines)      */
/* ------------------------------------------------------------------ */

// CSS design box; the tip sits on the bottom edge so `icon-anchor: "bottom"`
// pins the marker at the geographic point. Top/side margin leaves room for the
// SDF spread + white halo; the tip's tiny spread clips into the ground (unseen).
const CSS_MARGIN = 5;
const CSS_HEAD_RADIUS = 13;
const CSS_DOT_RADIUS = 4.5;
const CSS_WIDTH = CSS_HEAD_RADIUS * 2 + CSS_MARGIN * 2; // 36
const CSS_HEIGHT = PIN_NATIVE_HEIGHT; // 40 — tip at the bottom edge

/** Is (x, y) inside the triangle (a, b, c)? Barycentric sign test. */
function inTriangle(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): boolean {
  const d1 = (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const d2 = (px - cx) * (by - cy) - (bx - cx) * (py - cy);
  const d3 = (px - ax) * (cy - ay) - (cx - ax) * (py - ay);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

/** Teardrop membership at device scale `ratio`: the head disc unioned with the tip triangle. */
function pinInside(ratio: number): (x: number, y: number) => boolean {
  const hx = (CSS_WIDTH / 2) * ratio;
  const hy = (CSS_MARGIN + CSS_HEAD_RADIUS) * ratio;
  const R = CSS_HEAD_RADIUS * ratio;
  const tipX = (CSS_WIDTH / 2) * ratio;
  const tipY = CSS_HEIGHT * ratio;

  // Tangent points from the tip to the head circle bound the tail triangle.
  const dx = tipX - hx;
  const dy = tipY - hy;
  const dist = Math.hypot(dx, dy);
  const base = Math.atan2(dy, dx);
  const spread = Math.acos(Math.min(1, R / dist));
  const a1 = base + spread;
  const a2 = base - spread;
  const t1x = hx + R * Math.cos(a1);
  const t1y = hy + R * Math.sin(a1);
  const t2x = hx + R * Math.cos(a2);
  const t2y = hy + R * Math.sin(a2);

  return (x, y) => {
    if ((x - hx) * (x - hx) + (y - hy) * (y - hy) <= R * R) return true;
    return inTriangle(x, y, tipX, tipY, t1x, t1y, t2x, t2y);
  };
}

/** White head dot, positioned at the head centre so it overlays the tinted body exactly. */
function dotInside(ratio: number): (x: number, y: number) => boolean {
  const hx = (CSS_WIDTH / 2) * ratio;
  const hy = (CSS_MARGIN + CSS_HEAD_RADIUS) * ratio;
  const r = CSS_DOT_RADIUS * ratio;
  return (x, y) => (x - hx) * (x - hx) + (y - hy) * (y - hy) <= r * r;
}

/** The solid teardrop body SDF (tinted by color-by; haloed white in the layer). */
export function makePinImage(ratio: number = PIN_PIXEL_RATIO): SdfImage {
  return sdfFromMask(CSS_WIDTH * ratio, CSS_HEIGHT * ratio, pinInside(ratio));
}

/** The white head-dot SDF (same canvas box as the body → aligns at the same anchor/size). */
export function makePinDotImage(ratio: number = PIN_PIXEL_RATIO): SdfImage {
  return sdfFromMask(CSS_WIDTH * ratio, CSS_HEIGHT * ratio, dotInside(ratio));
}

/**
 * Pin geometry (CSS px, in the native `PIN_NATIVE_HEIGHT` box) that layers other
 * than the icon need — e.g. the selection halo, which must sit at the pin HEAD
 * (offset above the anchored tip), not on the ground at the tip.
 */
export const PIN_GEOMETRY = {
  nativeHeight: CSS_HEIGHT,
  headRadius: CSS_HEAD_RADIUS,
  /** Vertical distance from the anchored tip up to the head centre. */
  headCenterFromTip: CSS_HEIGHT - (CSS_MARGIN + CSS_HEAD_RADIUS),
} as const;

/* ------------------------------------------------------------------ */
/* FAIL-397 — center-anchored shape icons (square/diamond/…/star)      */
/* ------------------------------------------------------------------ */

/** The center-anchored SDF shapes (the point sits at the shape's centre, not a tip). */
export type SdfShape = "square" | "diamond" | "triangle" | "star" | "hexagon";

// Shapes share a square box sized to the pin head, so a square/diamond reads at a
// comparable weight to the teardrop. `icon-size 1` renders this box at SHAPE_NATIVE_SIZE.
const SHAPE_RADIUS = CSS_HEAD_RADIUS; // bounding radius (CSS) — outer vertices sit here
const SHAPE_BOX = SHAPE_RADIUS * 2 + CSS_MARGIN * 2; // 36
export const SHAPE_NATIVE_SIZE = SHAPE_BOX;

/** Center-shape geometry the halo needs: the native box + the shape's bounding radius. */
export const SHAPE_GEOMETRY = { nativeSize: SHAPE_BOX, boundingRadius: SHAPE_RADIUS } as const;

/** Even-odd ray-cast point-in-polygon — handles convex shapes AND the non-convex star. */
function inPolygon(px: number, py: number, verts: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const [xi, yi] = verts[i]!;
    const [xj, yj] = verts[j]!;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Polygon vertices from (angleDeg, radius) pairs about a device-scaled centre (y is down). */
function polyVerts(cx: number, cy: number, specs: Array<[number, number]>): Array<[number, number]> {
  return specs.map(([a, r]) => {
    const rad = (a * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
  });
}

/** The polygon (device px) for a shape centred in the SHAPE_BOX at scale `ratio`. */
function shapeVerts(shape: SdfShape, ratio: number): Array<[number, number]> {
  const c = (SHAPE_BOX / 2) * ratio;
  const R = SHAPE_RADIUS * ratio;
  switch (shape) {
    // Corners on the bounding circle at 45° → an axis-aligned square.
    case "square":
      return polyVerts(c, c, [45, 135, 225, 315].map((a) => [a, R]));
    // Points on the axes → a diamond (square rotated 45°).
    case "diamond":
      return polyVerts(c, c, [90, 180, 270, 0].map((a) => [a, R]));
    // Equilateral, apex up.
    case "triangle":
      return polyVerts(c, c, [90, 210, 330].map((a) => [a, R]));
    // Pointy-top regular hexagon.
    case "hexagon":
      return polyVerts(c, c, [90, 150, 210, 270, 330, 30].map((a) => [a, R]));
    // 5-point star: outer radius R every 72° from the top, inner 0.42R offset 36°.
    case "star": {
      const specs: Array<[number, number]> = [];
      for (let i = 0; i < 5; i++) {
        specs.push([90 + i * 72, R]);
        specs.push([90 + i * 72 + 36, R * 0.42]);
      }
      return polyVerts(c, c, specs);
    }
  }
}

/** A center-anchored shape SDF (tinted by color-by, haloed white — same as the pin body). */
export function makeShapeImage(shape: SdfShape, ratio: number = PIN_PIXEL_RATIO): SdfImage {
  const verts = shapeVerts(shape, ratio);
  return sdfFromMask(SHAPE_BOX * ratio, SHAPE_BOX * ratio, (x, y) => inPolygon(x, y, verts));
}

/**
 * A center-anchored filled-disc SDF (MAP-24). The classic `circle` marker is a
 * native `circle` layer, but shape-by mixes shapes in ONE symbol layer, so the
 * circle needs an SDF twin to sit alongside the polygon shapes. Sized in the same
 * SHAPE_BOX so it reads at a comparable weight and shares the center anchor.
 */
export function makeCircleImage(ratio: number = PIN_PIXEL_RATIO): SdfImage {
  const c = (SHAPE_BOX / 2) * ratio;
  const R = SHAPE_RADIUS * ratio;
  return sdfFromMask(SHAPE_BOX * ratio, SHAPE_BOX * ratio, (x, y) => (x - c) * (x - c) + (y - c) * (y - c) <= R * R);
}

/** Image ids the renderer registers + references (pin body/dot + the FAIL-397 shapes + the circle SDF). */
export const MARKER_IMAGE_IDS = {
  pin: PIN_IMAGE,
  pinDot: PIN_DOT_IMAGE,
  circle: "pz-circle",
  square: "pz-square",
  diamond: "pz-diamond",
  triangle: "pz-triangle",
  star: "pz-star",
  hexagon: "pz-hexagon",
} as const;

const SDF_SHAPES: SdfShape[] = ["square", "diamond", "triangle", "star", "hexagon"];

/** Every registerable SDF marker image (id → SdfImage), for a one-shot `addImage` on load. */
export function markerImages(ratio: number = PIN_PIXEL_RATIO): Array<{ id: string; image: SdfImage }> {
  return [
    { id: MARKER_IMAGE_IDS.pin, image: makePinImage(ratio) },
    { id: MARKER_IMAGE_IDS.pinDot, image: makePinDotImage(ratio) },
    { id: MARKER_IMAGE_IDS.circle, image: makeCircleImage(ratio) },
    ...SDF_SHAPES.map((s) => ({ id: MARKER_IMAGE_IDS[s], image: makeShapeImage(s, ratio) })),
  ];
}
